/**
 * Pure decision logic for the desktop screen-share audio capture strategy.
 * Extracted from voiceScreenShare so the fallback ordering is unit-testable
 * (the capture calls themselves are side-effectful and stay in the session).
 *
 * Strategies, best → worst:
 *   - 'app'          — WASAPI process-loopback of the shared window's process
 *                      tree only. No call echo, no other apps' sounds.
 *   - 'system-clean' — all PC audio EXCEPT Bunker's own process tree
 *                      (shells ≥1.0.30). No call echo, but includes every
 *                      other app's sound.
 *   - 'system-mix'   — Chromium's raw desktop loopback (legacy path): the
 *                      full system mix INCLUDING the call output. Listeners
 *                      may hear themselves — last resort only.
 */
export type ScreenAudioStrategy = 'app' | 'system-clean' | 'system-mix';

export interface ScreenAudioPlanInput {
  /** The shared source is a whole screen (`screen:*`), not a single window. */
  isScreen: boolean;
  /** The user asked for system audio directly — the picker's «Альтернативный
   *  звук» checkbox or the persistent voice preference. */
  forceSystem: boolean;
  /** Shell exposes per-window process capture (any desktop shell ≥1.0.24). */
  hasNativeApp: boolean;
  /** Shell exposes the exclude-self system capture (≥1.0.30). */
  hasNativeSystem: boolean;
}

/**
 * Ordered list of strategies to attempt. The session walks the list and uses
 * the first one that yields a live track.
 */
export function planScreenAudioStrategies(input: ScreenAudioPlanInput): ScreenAudioStrategy[] {
  const plan: ScreenAudioStrategy[] = [];
  // A single window's own audio is only meaningful (and only attempted) when
  // sharing a window and the user didn't explicitly ask for system audio.
  if (!input.isScreen && !input.forceSystem && input.hasNativeApp) {
    plan.push('app');
  }
  if (input.hasNativeSystem) {
    plan.push('system-clean');
  }
  // Legacy raw mix — the only option in browsers/old shells. Kept last so it
  // never shadows an echo-free native capture.
  plan.push('system-mix');
  return plan;
}
